// src/components/FAQSection.tsx
"use client";

import classNames from "classnames";
import { Minus, Plus } from "lucide-react";
import Script from "next/script";
import React, { useCallback, useMemo, useState } from "react";

import { useTranslations } from "@/context/TranslationsContext";

interface FaqItemData {
  question: string;
  answer: string;
}

const FAQItem = React.memo(function FAQItem({
  index,
  question,
  answer,
  isActive,
  toggle,
  isRTL,
}: {
  index: number;
  question: string;
  answer: string;
  isActive: boolean;
  toggle: (index: number) => void;
  isRTL: boolean;
}) {
  return (
    <li className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      <button
        className={classNames(
          "w-full flex justify-between items-start p-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-300 focus:outline-none",
          { "flex-row-reverse": isRTL },
        )}
        onClick={() => toggle(index)}
        aria-expanded={isActive}
        aria-controls={`faq-answer-${index}`}
        id={`faq-question-${index}`}
      >
        <span
          className={classNames("text-lg font-medium text-gray-700 flex-1", {
            "text-right": isRTL,
            "text-left": !isRTL,
            "ml-4": !isRTL,
            "mr-4": isRTL,
          })}
        >
          {question}
        </span>
        <span
          className={classNames("flex-shrink-0", {
            "mr-4": isRTL,
            "ml-4": !isRTL,
          })}
        >
          {isActive ? (
            <Minus
              className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
              aria-hidden="true"
            />
          ) : (
            <Plus
              className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
              aria-hidden="true"
            />
          )}
        </span>
      </button>
      {isActive && (
        <div
          id={`faq-answer-${index}`}
          role="region"
          aria-labelledby={`faq-question-${index}`}
          className="p-4 bg-white border-t border-gray-200 transition-all duration-300 ease-in-out"
        >
          <p
            className={classNames("text-gray-600", {
              "text-right": isRTL,
              "text-left": !isRTL,
            })}
          >
            {answer}
          </p>
        </div>
      )}
    </li>
  );
});

FAQItem.displayName = "FAQItem";

const FAQSection = () => {
  const { t, direction } = useTranslations();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isRTL = direction === "rtl";

  const toggleAccordion = useCallback((index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  }, []);

  const faqs: FaqItemData[] = useMemo(() => {
    const items: FaqItemData[] = [];
    for (let i = 1; i <= 15; i++) {
      items.push({
        question: t(`faqSection.faq${i}.question`),
        answer: t(`faqSection.faq${i}.answer`),
      });
    }
    return items;
  }, [t]);

  const schemaJSON = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    }),
    [faqs],
  );

  return (
    <>
      <section className="container mx-auto p-4 sm:p-6 lg:p-8 bg-white rounded-lg shadow-md mb-8 mt-8">
        <h2 className="text-3xl font-bold text-black mb-6">
          {t("faqSection.title")}
        </h2>
        <ul className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              index={index}
              question={faq.question}
              answer={faq.answer}
              isActive={activeIndex === index}
              toggle={toggleAccordion}
              isRTL={isRTL}
            />
          ))}
        </ul>
      </section>

      <Script
        type="application/ld+json"
        id="faq-schema"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJSON) }}
      />
    </>
  );
};

export default React.memo(FAQSection);
